import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { POLICIES } from "../data/policies";
import { SITE } from "../config";
import { usePageMeta } from "../lib/usePageMeta";
import { track } from "../lib/analytics";
import { smoothScrollTo } from "../lib/scroll";
import { OfficialPolicyDocument } from "../components/legal/OfficialPolicyDocument";
import { NotFoundPage } from "./InfoPages";

const renderBody = (body) =>
  body.map((b, i) => {
    if (b.p && b.ul) {
      return (
        <div key={i}>
          <ul>{b.ul.map((li) => <li key={li}>{highlight(li)}</li>)}</ul>
          <p>{highlight(b.p)}</p>
        </div>
      );
    }
    if (b.p) return <p key={i}>{highlight(b.p)}</p>;
    if (b.ul) return <ul key={i}>{b.ul.map((li) => <li key={li}>{highlight(li)}</li>)}</ul>;
    return null;
  });

const highlight = (text) => {
  const parts = String(text).split(/(\[[^\]]+\])/g);
  return parts.map((p, i) => (p.startsWith("[") && p.endsWith("]") ? <span key={i} className="placeholder">{p}</span> : p));
};

export default function PolicyPage() {
  const { slug } = useParams();
  const policy = POLICIES[slug];
  usePageMeta(policy ? `${policy.title} — HUMN` : "HUMN", policy?.description || "");

  useEffect(() => {
    if (policy) track("policy_opened", { policy: slug });
  }, [slug, policy]);

  if (!policy) return <NotFoundPage />;

  return (
    <main className="scene scene-purple pt-36 pb-28 px-6 min-h-screen" data-testid={`policy-page-${slug}`}>
      <div className="max-w-5xl mx-auto">
        <Link to="/policies" className="eyebrow mb-4 inline-flex hover:opacity-70 transition-opacity duration-200" data-testid={`policy-back-to-directory-${slug}`}>HUMN · Trust centre · All policies</Link>
        <h1 className="h-display h-lg" data-testid={`policy-title-${slug}`}>{policy.fullTitle || policy.title}</h1>
        {policy.featured && <p className="master-policy-badge mt-6" data-testid="master-policy-highlight">Master governance document · Overriding policy framework</p>}
        <p className="tx-2 text-sm mt-4" data-testid="policy-last-updated">
          {policy.official ? `Effective ${policy.effectiveDate} · Last updated ${policy.lastUpdated}` : `Last updated: ${SITE.policyEffectiveDate}`}
        </p>

        {policy.official ? <OfficialPolicyDocument policy={policy} slug={slug} /> : <div className="grid lg:grid-cols-12 gap-14 mt-16">
          <aside className="hidden lg:block lg:col-span-4">
            <nav aria-label="Table of contents" className="sticky top-32">
              <p className="eyebrow mb-5">On this page</p>
              <ul className="space-y-2.5 border-l pl-4" style={{ borderColor: "var(--border-glass)" }}>
                {policy.sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`toc-link-${s.id}`}
                      onClick={(e) => { e.preventDefault(); smoothScrollTo(document.getElementById(s.id)); }}
                      className="text-[13px] tx-2 hover:opacity-100 transition-opacity duration-200"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
          <article className="lg:col-span-8 policy-prose max-w-2xl">
            {policy.sections.map((s) => (
              <section key={s.id}>
                <h2 id={s.id}>{s.title}</h2>
                {renderBody(s.body)}
              </section>
            ))}
            <div className="glass-2 mt-16 rounded-3xl p-8">
              <h2 className="!mt-0">Questions?</h2>
              <p>
                Contact <a href={`mailto:${SITE.contactEmail}`} className="underline underline-offset-4" data-testid={`policy-contact-email-${slug}`}>{SITE.contactEmail}</a> or visit our{" "}
                <Link to="/support" className="underline underline-offset-4" data-testid={`policy-support-link-${slug}`}>Support page</Link>.
              </p>
              <p className="!mb-0 text-sm">{SITE.companyName} · {highlight(SITE.legalAddress)}</p>
            </div>
          </article>
        </div>}
      </div>
    </main>
  );
}
